<script setup lang="ts">
import type { ShopCommodityLite } from 'ld-shop-sdk'
import { LdImagePreview } from '#components'
import { useMediaShare } from 'ld-share-sdk'
import { useShopRoutePath } from 'ld-shop-sdk'
import type { AppDetail } from '~/types'
import type { EasyfunGameJson } from '~/types/easyfun'

interface Props {
  value: AppDetail
  commidity: ShopCommodityLite | undefined
  easyfun: EasyfunGameJson | undefined
}

const props = defineProps<Props>()

const swiperInstance = ref()
const swiperAttr = reactive<Record<string, any>>({
  modules: [SwiperNavigation],
  navigation: {
    disabledClass: 'pointer-events-none c-op-30',
    lockClass: 'hidden',
    prevEl: '.prev',
    nextEl: '.next',
  },
  slidesPerView: 'auto',
  slidesPerGroup: 1,
  // render渲染时候有问题，直接在css上加
  spaceBetween: 20,
})

const { toEasyfunWeb, toDeveloper } = useRouterToPage()
const { toCommodityDetail } = useShopRoutePath()

const { getXLink, getApkLink } = useDownload()
const gameLink = getXLink({ pkg: props.value.packageName })
const apkLink = getApkLink({ pkg: props.value.packageName, url: props.value.apk })

const imgPreviewRef = ref<InstanceType<typeof LdImagePreview>>()

const { isWindows, isAndroid } = useDevice()
const { windowScrollY, setConf } = useLayout()
const el = ref()
const visible = useElementVisibility(el)
watch(visible, v => setConf({ sidebar: { share: true, visible: !v } }), { immediate: true })

const showDrawer = computed(() => isAndroid && !!windowScrollY.value && !visible.value)
watch(showDrawer, v => setConf({ sidebar: { bottomOffset: v ? 44 : 0 } }), { immediate: true })

const { clickShare } = useMediaShare({ fbAppId: '160640943743010' })
const { openshareDialog } = useDialog()

const logReportParams = computed(() => ({ name: props.value.gameName, seo: props.value.seo, pkg: props.value.packageName }))
</script>

<template>
  <div class="of-hidden rd-8px md:bg-#fff">
    <div
      class="flex flex-col gap-20px"
      md="h-435px p-40px pt-60px bg-gradient-to-t stops-[#5151D926_0%,#5151D922_30%,#5151D913_54%,transparent_76%]"
    >
      <div
        ref="el"
        class="grid grid-areas-[icon_name]-[icon_develop]-[version_version]-[tag_tag]-[play_play]-[share_share] md:grid-areas-[icon_name_play]-[icon_version_play]-[icon_develop_play]-[icon_tag_play]-[share_share_share]"
        md="gap-[0_24px] items-center grid-cols-[auto_1fr_auto]"
        lt-md="rd-8px gap-[0_16px] items-center grid-cols-[auto_1fr] p-20px pt-14px bg-#fff bg-gradient-to-t stops-[#5151D926_0%,#5151D922_30%,#5151D913_54%,transparent_76%]"
      >
        <div class="grid-area-[icon] of-hidden rd-10px" size="78px md:126px">
          <LdImage :src="scaleImage(value.icon, 256)" class="size-full" :alt="value.gameName" loading="lazy" />
        </div>

        <h1
          class="grid-area-[name] c-#161636 font-900"
          lt-md="line-clamp-2 text-18px lh-26px"
          md="line-clamp-1 text-28px lh-36px"
        >
          {{ $t('game.title', { game: value.gameName }) }}
        </h1>

        <div
          class="grid-area-[version] c-#161636 c-op-70"
          lt-md="text-12px grid mt-16px justify-between grid-cols-2 gap-8px"
          md="text-16px flex flex-wrap gap-[4px_8px]"
        >
          <template v-if="value.version">
            <span>{{ $t('game.version') }}:</span>
            <span class="md:mie-16px lt-md:text-end">{{ value.version }}</span>
          </template>
          <template v-if="value.apkUpdate">
            <span>{{ $t('game.last_update') }}: </span>
            <span class="lt-md:text-end">{{ value.apkUpdate }}</span>
          </template>
        </div>

        <div class="grid-area-[develop] gap-12px lh-1.5em" lt-md="text-12px" md="text-16px">
          <LdRateLite class="mie-16px gap-6px c-#161636 c-op-70 svg:v-base" :value="value.score || 4" />
          <NuxtLink v-if="value.developer" c="purple-5 hover:purple-6 active:purple-7" :to="toDeveloper(value.developer, { hasReturnUrl: true })">
            {{ value.developer }}
          </NuxtLink>
        </div>

        <div class="grid-area-[tag] flex items-center">
          <GameTypePill class="w-fit lt-md:mt-16px" :name="value.typeName" :type="value.coding" />
          <GameTypePill v-if="value.status === 7" class="mis-8px w-fit lt-md:mt-16px" :name="$t('game.pre_registration')" type="upcoming-games" />
          <ClientOnly>
            <span v-if="easyfun" class="hidden md:inline">
              <span class="mx-16px c-#161636 c-op-20">|</span>
              <Icon name="EasyfunLogo" class="mie-8px rd-8px v-sub text-22px" b="~ solid #0D0B24 op-10" />
              <a v-track="{ id: 'web-apps-detail-play-online', click: { value: logReportParams } }" :href="toEasyfunWeb(easyfun)" class="text-12px" c="purple-5 hover:purple-6 active:purple-7" target="_blank">
                {{ $t('game.play_online') }}
              </a>
            </span>
          </ClientOnly>
        </div>

        <div class="grid-area-[play] flex-center flex-col gap-16px md:max-w-250px" lt-md="mt-16px">
          <ClientOnly>
            <template v-if="isWindows">
              <a v-track="{ id: 'web-apps-detail-play-exe', click: { value: logReportParams } }" :href="gameLink" class="ld-button h-fit min-w-134px w-full rd-8px px-24px py-12px text-16px font-700 ld-button-yellow">
                {{ $t('game.play_on_oc') }}
              </a>
            </template>
            <template v-else-if="isAndroid">
              <a v-track="{ id: 'web-apps-detail-play-apk', click: { value: logReportParams } }" :href="apkLink" class="ld-button h-fit min-w-134px w-full rd-8px px-24px py-12px text-16px font-700 ld-button-yellow">
                {{ $t('game.download_apk') }}
              </a>
            </template>
            <template v-else>
              <button class="ld-button h-fit min-w-134px w-full rd-8px px-12px py-10px text-center text-16px font-700 lh-20px ld-button-yellow" disabled>
                {{ $t('game.not_support_device') }}
              </button>
            </template>

            <template #fallback>
              <a :href="gameLink" class="ld-button h-fit min-w-134px w-full rd-8px px-24px py-12px text-16px font-700 ld-button-yellow">
                {{ $t('game.play_on_oc') }}
              </a>
            </template>
          </ClientOnly>

          <a
            v-if="commidity"
            v-track="{ id: 'web-apps-detail-shop', click: { value: logReportParams } }" :href="toCommodityDetail(commidity)" target="_blank"
            class="ld-button h-fit min-w-133px w-full flex-col gap-4px rd-8px px-24px py-6px text-16px font-700 ld-button-purple-outline"
            lt-md="mt-16px"
          >
            <span>{{ $t('game.recharge') }}</span>
            <span class="text-12px">{{ commidity.maxDiscount }}</span>
          </a>
        </div>

        <ClientOnly>
          <span v-if="easyfun" v-track="{ id: 'web-apps-detail-play-online', click: { value: logReportParams } }" class="grid-col-[span_2] mt-20px flex-center md:hidden">
            <Icon name="EasyfunLogo" class="mie-8px rd-6px text-22px" b="~ solid #0D0B24 op-10" />
            <a :href="toEasyfunWeb(easyfun)" class="text-14px" c="purple-5 hover:purple-6 active:purple-7" target="_blank">
              {{ $t('game.play_online') }}
            </a>
          </span>
        </ClientOnly>

        <ul class="grid-area-[share] flex" lt-md="justify-center mt-24px gap-8px" md="justify-end gap-12px mt-10px">
          <li v-track="{ id: 'web-apps-detail-share', click: { value: { type: 'facebook', ...logReportParams } } }" class="m-0 b-none bg-transparent p-0" size="32px md:28px" data-share="facebook" aria-label="share to facebook" @click="clickShare">
            <Icon name="ShareFaceBook" b="~ solid #000 op-12" c="#161636 op-70" class="size-full cursor-pointer rd-4px transition-all-100" hover="b-transparent bg-#0C8CF4  c-#fff" />
          </li>

          <li v-track="{ id: 'web-apps-detail-share', click: { value: { type: 'X', ...logReportParams } } }" class="m-0 b-none bg-transparent p-0" size="32px md:28px" data-share="X" aria-label="share to X" @click="clickShare">
            <Icon name="ShareX" b="~ solid #000 op-12" c="#161636 op-70" class="size-full cursor-pointer rd-4px transition-all-100" hover="b-transparent bg-#111  c-#fff" />
          </li>

          <li v-track="{ id: 'web-apps-detail-share', click: { value: { type: 'reddit', ...logReportParams } } }" class="m-0 b-none bg-transparent p-0" size="32px md:28px" data-share="reddit" aria-label="share to reddit" @click="clickShare">
            <Icon name="ShareReddit" b="~ solid #000 op-12" c="#161636 op-70" class="size-full cursor-pointer rd-4px transition-all-100" hover="b-transparent  bg-#F94040  c-#fff" />
          </li>

          <li class="m-0 b-none bg-transparent p-0" size="32px md:28px" @click="openshareDialog()">
            <Icon name="ShareOther" b="~ solid #000 op-12" c="#161636 op-70" class="size-full cursor-pointer rd-4px transition-all-100" hover="b-transparent bg-purple-5  c-#fff" />
          </li>
        </ul>
      </div>

      <div class="relative mx--20px flex-1 px-20px">
        <Swiper
          class="rd-8px" :class="!swiperInstance && '[&>.swiper-wrapper]:flex [&>.swiper-wrapper]:gap-[0_20px] [&>.swiper-wrapper]:of-hidden'"
          v-bind="swiperAttr" @swiper="swiperInstance = $event"
        >
          <SwiperSlide v-for="img in value.headBanner" :key="img" class="group relative cursor-pointer of-hidden rd-8px w-fit!" @click="imgPreviewRef?.open({ src: img })">
            <div class="absolute z-2 size-full bg-#000 bg-op-30 op-0 transition-all-300 group-hover:op-100" />
            <LdImage class="h-170px w-auto" skeleton-class="aspect-ratio-16/9" :src="scaleImage(img, [512, 0])" alt="" loading="lazy" />
          </SwiperSlide>
        </Swiper>

        <LdSwiperSlideButton class="prev inset-is-0 text-24px lt-md:hidden" turn="left" bg="#000 op-40" c="#fff hover:purple-5" @click="swiperInstance?.slidePrev()" />
        <LdSwiperSlideButton class="next inset-ie-0 text-24px lt-md:hidden" turn="right" bg="#000 op-40" c="#fff hover:purple-5" @click="swiperInstance?.slideNext()" />
      </div>
    </div>

    <ClientOnly>
      <div class="fixed bottom-0 left-0 z-10 w-full">
        <LdFold :visible="showDrawer" shadow="[0px_2px_8px_0px_#1F1E3D33]">
          <div class="w-full flex items-center gap-12px bg-#fff px-20px py-12px">
            <LdImage :src="scaleImage(value.icon, 256)" class="size-48px flex-shrink-0 rd-8px" :alt="value.gameName" loading="lazy" />
            <p class="line-clamp-2 flex-1 text-14px font-700 lh-20px">
              {{ value.gameName }}
            </p>
            <a :href="apkLink" class="ld-button h-fit min-w-92px w-fit flex-shrink-0 rd-4px px-24px py-12px text-12px font-700 ld-button-yellow">
              {{ $t('game.download_apk') }}
            </a>
          </div>
        </LdFold>
      </div>

      <Teleport to="body">
        <LdImagePreview ref="imgPreviewRef" />
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
</style>
